import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { employeeApi } from "../employeeApi";
import { queryKeys } from "./queryKeys";
import useInvalidatingMutation from "./useInvalidatingMutation";

/* ---------- Lists & counts ---------- */
/** Employee list / picker search (the server limits BRANCH-scope roles to their managed branches). */
export const useEmployees = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.employees.list(filters),
    queryFn: () => employeeApi.getList(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useEmployeeCount = (filters) =>
  useQuery({
    queryKey: queryKeys.employees.count(filters),
    queryFn: () => employeeApi.getCount(filters),
    enabled: !!filters?.companyId,
  });

/* ---------- One employee ---------- */
export const useEmployee = (id) =>
  useQuery({
    queryKey: queryKeys.employees.detail(id),
    queryFn: () => employeeApi.getById(id),
    enabled: !!id,
  });

export const useEmployeeImage = (id) =>
  useQuery({
    queryKey: queryKeys.employees.image(id),
    queryFn: () => employeeApi.getImage(id),
    enabled: !!id,
    retry: false, // 404 when the employee has no photo yet
  });

export const useCreateEmployee = () =>
  useInvalidatingMutation(
    (data) => employeeApi.create(data),
    [queryKeys.employees.all],
  );

export const useUpdateEmployee = () =>
  useInvalidatingMutation(
    ({ id, data }) => employeeApi.update(id, data),
    [queryKeys.employees.all],
  );

/** Recalculate Complete/Incomplete status, then refresh the employee and lists. */
export const useCheckCompleteness = () =>
  useInvalidatingMutation(
    (id) => employeeApi.checkCompleteness(id),
    [queryKeys.employees.all],
  );

export const useUploadEmployeeImage = () =>
  useInvalidatingMutation(
    ({ employeeId, file }) => employeeApi.uploadImage(employeeId, file),
    (_data, { employeeId }) => [queryKeys.employees.image(employeeId)],
  );

/* ---------- Bulk import (background job) ---------- */
export const IMPORT_POLL_INTERVAL = 2000;
const MAX_BACKOFF = 15000;
const MAX_POLL_RETRIES = 40; // ~10 minutes of failed polls before giving up

const isTransientError = (error) => {
  const status = error?.response?.status;
  return !status || status >= 500; // network error or server error
};

/** Starts the import. Nothing is saved yet when this resolves, so no lists are refreshed. */
export const useStartEmployeeImport = () =>
  useMutation({ mutationFn: (args) => employeeApi.startImport(args) });

/**
 * Progress of one import batch.
 * - Polls every 2s while status is PROCESSING, then stops.
 * - Network/5xx errors: keeps the last progress on screen and retries with
 *   backoff (2s, 4s, 8s, max 15s) instead of showing the import as failed.
 * - 404 (unknown batch) and other 4xx: no retry, the error is shown.
 * - Polling stops automatically when the page unmounts.
 */
export const useEmployeeImportStatus = (batchId) =>
  useQuery({
    queryKey: queryKeys.employeeImport.batch(batchId),
    queryFn: () => employeeApi.getImportStatus(batchId),
    enabled: !!batchId,
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "PROCESSING" ? IMPORT_POLL_INTERVAL : false,
    retry: (failureCount, error) =>
      isTransientError(error) && failureCount < MAX_POLL_RETRIES,
    // failureCount starts at 0 → 2s, 4s, 8s, then capped at 15s
    retryDelay: (failureCount) =>
      Math.min(IMPORT_POLL_INTERVAL * 2 ** failureCount, MAX_BACKOFF),
  });
